/**
 * Client-side chat rate limit using localStorage (rolling 1-hour window).
 * Optional; max requests per hour is set from PHP via wbAiChatConfig.rateLimitPerHour.
 */
const MS_PER_HOUR = 60 * 60 * 1000;
/** localStorage key: ms since epoch (string) when sends are blocked until */
const RATE_LIMIT_UNTIL_KEY = 'wb_ai_chat_ratelimit_until';
/** localStorage key: JSON array of send timestamps (number, ms since epoch) */
const RATE_LIMIT_TIMES_KEY = 'wb_ai_chat_ratelimit_times';
/**
 * Maximum sends allowed per hour from config (0 = disabled).
 * Coerces string values (e.g. from wp_localize_script) to integers.
 *
 * @param config WB AI Chat frontend config.
 * @returns Non-negative integer cap.
 */
export function getRateLimitPerHour(config) {
    const raw = config.rateLimitPerHour;
    if (raw === undefined || raw === null || raw === '') {
        return 0;
    }
    const n = typeof raw === 'number'
        ? raw
        : Number.parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n) || n <= 0) {
        return 0;
    }
    return Math.floor(n);
}
/**
 * Read parsed until timestamp (ms), or null if missing/invalid.
 */
function readUntilMs() {
    try {
        const raw = localStorage.getItem(RATE_LIMIT_UNTIL_KEY);
        if (raw === null || raw === '') {
            return null;
        }
        const n = Number.parseInt(raw, 10);
        if (!Number.isFinite(n)) {
            return null;
        }
        return n;
    }
    catch {
        return null;
    }
}
/**
 * Remove expiry key if it has passed.
 */
function clearUntilIfExpired() {
    const until = readUntilMs();
    if (until !== null && Date.now() >= until) {
        try {
            localStorage.removeItem(RATE_LIMIT_UNTIL_KEY);
        }
        catch {
            /* ignore */
        }
    }
}
/**
 * Persist block-until time (ms since epoch).
 *
 * @param untilMs Timestamp after which sends may proceed again.
 */
function setRateLimitUntil(untilMs) {
    try {
        localStorage.setItem(RATE_LIMIT_UNTIL_KEY, String(Math.floor(untilMs)));
    }
    catch {
        /* ignore */
    }
}
/**
 * Load send timestamps, prune entries older than one hour, optionally write back.
 *
 * @param now Current time ms.
 * @returns Timestamps within the rolling hour.
 */
function getPrunedTimes(now) {
    try {
        const raw = localStorage.getItem(RATE_LIMIT_TIMES_KEY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        const cutoff = now - MS_PER_HOUR;
        const times = parsed
            .map((t) => (typeof t === 'number' ? t : Number.NaN))
            .filter((t) => Number.isFinite(t) && t > cutoff);
        try {
            localStorage.setItem(RATE_LIMIT_TIMES_KEY, JSON.stringify(times));
        }
        catch {
            /* ignore */
        }
        return times;
    }
    catch {
        return [];
    }
}
/**
 * If the rolling window already has max sends, set expiry to when the oldest falls out.
 *
 * @param times Pruned timestamps in the last hour.
 * @param limit Max sends per hour.
 */
function applyWindowExceededUntil(times, limit) {
    if (times.length < limit) {
        return;
    }
    const oldest = Math.min(...times);
    setRateLimitUntil(oldest + MS_PER_HOUR);
}
/**
 * Whether the user is currently blocked (without recording a new send).
 * Updates localStorage: prunes times, clears expired until, may set until if window is full.
 *
 * @param config Frontend config.
 * @returns True if chat sends should be blocked.
 */
export function isCurrentlyRateLimited(config) {
    const limit = getRateLimitPerHour(config);
    if (limit <= 0) {
        return false;
    }
    clearUntilIfExpired();
    const now = Date.now();
    const until = readUntilMs();
    if (until !== null && now < until) {
        return true;
    }
    const times = getPrunedTimes(now);
    if (times.length >= limit) {
        applyWindowExceededUntil(times, limit);
        return true;
    }
    return false;
}
/**
 * Record one user send if allowed; otherwise set expiry and return false.
 *
 * @param config Frontend config.
 * @returns Whether the send may proceed.
 */
export function tryConsumeRateLimitSlot(config) {
    const limit = getRateLimitPerHour(config);
    if (limit <= 0) {
        return true;
    }
    clearUntilIfExpired();
    const now = Date.now();
    let until = readUntilMs();
    if (until !== null && now < until) {
        return false;
    }
    let times = getPrunedTimes(now);
    if (times.length >= limit) {
        applyWindowExceededUntil(times, limit);
        return false;
    }
    times = [...times, now];
    try {
        localStorage.setItem(RATE_LIMIT_TIMES_KEY, JSON.stringify(times));
    }
    catch {
        /* ignore */
    }
    if (times.length >= limit) {
        applyWindowExceededUntil(times, limit);
    }
    return true;
}
